import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Users, Plus, Gift, CalendarDays, Pencil, Trash2 } from "lucide-react";
import { Modal } from "../components/Modal";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { useConfig, symbolForCurrency } from "../lib/format";
import { PageHeader, EmptyState, Loader } from "../components/shared";
import { MoneyInput } from "../components/MoneyInput";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const diff = Math.ceil((d - new Date()) / 86400000);
  return diff;
}

function GroupEditModal({ group, onClose, onSaved }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const symbol = symbolForCurrency(config, group.currency || user?.currency || "BRL");
  const [form, setForm] = useState({ name: group.name || "", exchange_date: group.exchange_date || "", suggested_value: group.suggested_value ?? "", message: group.message || "" });
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold";
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.put(`/secret-santa/groups/${group.group_id}`, { name: form.name, exchange_date: form.exchange_date || null, suggested_value: form.suggested_value !== "" ? Number(form.suggested_value) : null, message: form.message || null });
      toast.success(t("ss.updated")); onSaved(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    setSaving(false);
  };
  return (
    <Modal open onClose={onClose} title={t("ss.edit_title")} testid="ss-edit-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="ss-edit-form">
        <div><label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.group_name")}</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ss-edit-name" className={inputCls} /></div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.exchange_date")}</label>
            <input type="date" value={form.exchange_date} onChange={(e) => setForm({ ...form, exchange_date: e.target.value })} data-testid="ss-edit-date" className={inputCls} /></div>
          <div><label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.suggested_value")} ({symbol})</label>
            <MoneyInput testid="ss-edit-value" value={form.suggested_value} onChange={(v) => setForm({ ...form, suggested_value: v })} placeholder="100,00" className={inputCls} /></div>
        </div>
        <div><label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.group_message")}</label>
          <textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ss-edit-message" className={inputCls} /></div>
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} data-testid="ss-edit-cancel" className="rounded-full border border-xmas-gold/30 px-5 py-2.5 text-sm text-xmas-snow hover:border-xmas-gold">{t("ss.cancel")}</button>
          <button type="submit" disabled={saving} data-testid="ss-edit-save" className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold uppercase tracking-wider disabled:opacity-60">{saving ? "…" : t("ss.save_changes")}</button>
        </div>
      </form>
    </Modal>
  );
}

export default function SecretSantaList() {
  const { t } = useI18n();
  const [groups, setGroups] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/secret-santa/groups").then(({ data }) => setGroups(data.groups)).catch(() => setGroups([]));
  }, []);

  const removeGroup = async () => {
    if (!confirmDel || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/secret-santa/groups/${confirmDel.group_id}`);
      setGroups((gs) => gs.filter((g) => g.group_id !== confirmDel.group_id));
      toast.success(t("ss.deleted")); setConfirmDel(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    setDeleting(false);
  };

  if (groups === null) return <Loader />;

  return (
    <div>
      <PageHeader title={t("ss.my_groups")} subtitle={t("ss.subtitle")} testid="ss-list-header" />
      <Link to="/app/secret-santa/new" data-testid="ss-create-link" className="btn-gold mb-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider">
        <Plus className="h-4 w-4" /> {t("ss.create_btn")}
      </Link>

      {groups.length === 0 ? (
        <EmptyState icon={Gift} title={t("ss.empty")} subtitle={t("ss.empty_sub")} testid="ss-empty" />
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => {
            const d = daysUntil(g.exchange_date);
            return (
              <div key={g.group_id} className="card-lift glass relative rounded-2xl p-6" data-testid={`ss-group-card-${g.group_id}`}>
                <div className="mb-3 flex items-center justify-between">
                  <Users className="h-6 w-6 text-xmas-gold" />
                  <div className="flex items-center gap-1">
                    {g.drawn && <span className="mr-1 rounded-full bg-xmas-gold/20 px-2 py-0.5 text-xs text-xmas-gold">{t("ss.drawn_badge")}</span>}
                    {g.is_owner && (
                      <>
                        <button type="button" onClick={() => setEditing(g)} aria-label={t("ss.edit_group")} title={t("ss.edit_group")} data-testid={`ss-group-edit-${g.group_id}`} className="rounded-full p-1.5 text-xmas-slate transition hover:bg-white/10 hover:text-xmas-gold"><Pencil className="h-4 w-4" /></button>
                        <button type="button" onClick={() => setConfirmDel(g)} aria-label={t("ss.delete_group")} title={t("ss.delete_group")} data-testid={`ss-group-delete-${g.group_id}`} className="rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson"><Trash2 className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                </div>
                <Link to={`/app/secret-santa/${g.group_id}`} data-testid={`ss-group-${g.group_id}`} className="block">
                <h3 className="font-display text-lg text-xmas-snow">{g.name}</h3>
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-xmas-slate">
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{g.participant_count} {t("ss.count")}</span>
                  {g.exchange_date && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{g.exchange_date}</span>}
                </div>
                {d != null && d >= 0 && (
                  <p className="mt-3 font-serif-elegant text-sm italic text-xmas-champagne">
                    {d === 0 ? t("ss.countdown_today") : t("ss.countdown", { n: d })}
                  </p>
                )}
                </Link>
              </div>
            );
          })}
        </div>
      )}
      {editing && <GroupEditModal group={editing} onClose={() => setEditing(null)} onSaved={(g) => { setGroups((gs) => gs.map((x) => x.group_id === g.group_id ? { ...x, ...g } : x)); setEditing(null); }} />}
      {confirmDel && (
        <Modal open onClose={() => setConfirmDel(null)} title={t("ss.delete_group_q")} testid="ss-delete-modal">
          <p className="text-sm text-xmas-slate">{t("ss.delete_group_note")}</p>
          <p className="mt-2 font-display text-lg text-xmas-snow">{confirmDel.name}</p>
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setConfirmDel(null)} data-testid="ss-delete-cancel" className="rounded-full border border-xmas-gold/30 px-5 py-2.5 text-sm text-xmas-snow hover:border-xmas-gold">{t("ss.cancel")}</button>
            <button type="button" onClick={removeGroup} disabled={deleting} data-testid="ss-delete-confirm" className="inline-flex items-center gap-2 rounded-full bg-xmas-crimson px-5 py-2.5 text-sm font-bold uppercase text-white hover:brightness-110 disabled:opacity-60"><Trash2 className="h-4 w-4" /> {deleting ? "…" : t("ss.delete_group_yes")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function SecretSantaCreate() {
  const { t } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", exchange_date: "", suggested_value: "", message: "", include_me: true });
  const [loading, setLoading] = useState(false);
  const symbol = symbolForCurrency(config, user?.currency || "BRL");
  const inputCls = "w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/secret-santa/groups", {
        name: form.name,
        exchange_date: form.exchange_date || null,
        suggested_value: form.suggested_value ? Number(form.suggested_value) : null,
        currency: user?.currency || "BRL",
        message: form.message || null,
        include_me: form.include_me,
      });
      navigate(`/app/secret-santa/${data.group_id}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title={t("ss.create")} testid="ss-create-header" />
      <form onSubmit={submit} className="glass max-w-xl space-y-5 rounded-2xl p-6 sm:p-8" data-testid="ss-create-form">
        <div>
          <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.group_name")}</label>
          <input data-testid="ss-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("ss.group_name_ph")} className={inputCls} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.exchange_date")}</label>
            <input data-testid="ss-date-input" type="date" value={form.exchange_date} onChange={(e) => setForm({ ...form, exchange_date: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.suggested_value")} ({symbol})</label>
            <MoneyInput testid="ss-value-input" value={form.suggested_value} onChange={(v) => setForm({ ...form, suggested_value: v })} placeholder="100,00" className={inputCls} />
          </div>
        </div>
        <div>
          <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.group_message")}</label>
          <textarea data-testid="ss-message-input" rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("ss.group_message_ph")} className={inputCls} />
        </div>
        <label className="flex items-center gap-2 text-sm text-xmas-snow">
          <input type="checkbox" checked={form.include_me} onChange={(e) => setForm({ ...form, include_me: e.target.checked })} className="h-4 w-4 accent-[#D4AF37]" data-testid="ss-includeme-checkbox" />
          {t("ss.include_me")}
        </label>
        <button type="submit" disabled={loading} data-testid="ss-create-submit" className="btn-gold rounded-xl px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
          {loading ? "..." : t("ss.create_btn")}
        </button>
      </form>
    </div>
  );
}

export function SecretSantaJoin() {
  const { t } = useI18n();
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/secret-santa/invite/${token}`).then(({ data }) => setInfo(data)).catch(() => setError(true));
  }, [token]);

  const join = async () => {
    if (user === false) {
      localStorage.setItem("mc_pending_invite", token);
      navigate("/login");
      return;
    }
    try {
      const { data } = await api.post(`/secret-santa/join/${token}`);
      toast.success(t("ss.joined"));
      navigate(`/app/secret-santa/${data.group_id}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (error) return <div className="flex min-h-screen items-center justify-center bg-xmas-navy text-xmas-slate">Invalid invite</div>;
  if (!info) return <div className="flex min-h-screen items-center justify-center bg-xmas-navy"><div className="h-10 w-10 animate-spin rounded-full border-2 border-xmas-gold/30 border-t-xmas-gold" /></div>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-xmas-navy px-4">
      <div className="glass max-w-md rounded-3xl p-8 text-center" data-testid="ss-join-card">
        <Gift className="mx-auto mb-4 h-12 w-12 text-xmas-gold" />
        <h1 className="font-display text-2xl text-xmas-snow">{t("ss.join_title")}</h1>
        <p className="mt-4 text-sm text-xmas-slate">{t("ss.join_msg", { owner: info.owner_name || t("ss.organizer"), group: info.group_name })}</p>
        <button onClick={join} data-testid="ss-join-button" className="btn-gold mt-6 w-full rounded-xl py-3.5 text-sm font-bold uppercase tracking-wider">
          {t("ss.join_btn")}
        </button>
      </div>
    </div>
  );
}
